import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCurrentUserContext } from '../services/datasetsApi';

const getStoredToken = () =>
  localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token') || '';

const getStoredUserId = () => localStorage.getItem('user_id') || '';
const getStoredWorkspaceId = () => localStorage.getItem('workspace_id') || '';

const useSessionContext = () => {
  const [token] = useState(getStoredToken);
  const [userId, setUserId] = useState(getStoredUserId);
  const [workspaceId, setWorkspaceId] = useState(getStoredWorkspaceId);
  const [isResolvingContext, setIsResolvingContext] = useState(false);

  const persistContext = useCallback((nextUserId, nextWorkspaceId) => {
    if (nextUserId) {
      localStorage.setItem('user_id', nextUserId);
      setUserId(nextUserId);
    }

    if (nextWorkspaceId) {
      localStorage.setItem('workspace_id', String(nextWorkspaceId));
      setWorkspaceId(String(nextWorkspaceId));
    } else {
      localStorage.removeItem('workspace_id');
      setWorkspaceId('');
    }
  }, []);

  const refreshContext = useCallback(async () => {
    if (!token) {
      return { userId: '', workspaceId: '' };
    }

    setIsResolvingContext(true);
    try {
      const context = await fetchCurrentUserContext();
      persistContext(context.userId, context.workspaceId);
      return {
        userId: context.userId ? String(context.userId) : '',
        workspaceId: context.workspaceId ? String(context.workspaceId) : ''
      };
    } catch (error) {
      return {
        userId: getStoredUserId(),
        workspaceId: getStoredWorkspaceId()
      };
    } finally {
      setIsResolvingContext(false);
    }
  }, [persistContext, token]);

  useEffect(() => {
    if (!token || (userId && workspaceId)) {
      return;
    }

    refreshContext();
  }, [token, userId, workspaceId, refreshContext]);

  return useMemo(() => ({
    token,
    userId,
    workspaceId,
    isResolvingContext,
    refreshContext
  }), [token, userId, workspaceId, isResolvingContext, refreshContext]);
};

export default useSessionContext;
